// src/components/Profile/FiscalPreferencesManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

const REGIMES = [
  { v: "", labelKey: "fiscal.regime.none" },
  { v: "FR_FORFAIT_6_5", labelKey: "fiscal.regime.FR_FORFAIT_6_5" },
  { v: "FR_PLUS_VALUE", labelKey: "fiscal.regime.FR_PLUS_VALUE" },
  { v: "UK_CGT_SIMPLE_20", labelKey: "fiscal.regime.UK_CGT_SIMPLE_20" },
  { v: "DE_PRIVATE_1Y_RULE", labelKey: "fiscal.regime.DE_PRIVATE_1Y_RULE" },
];

/**
 * FiscalPreferencesManager
 * - Expert / Family Office : sélection d'un client -> lecture/édition de client_fiscal_prefs
 * - Peut aussi être forcé via prop clientId
 */
export default function FiscalPreferencesManager({ clientId = null }) {
  const { user, profile } = useAuth() || {};
  const { t } = useTranslation();

  const role = String(profile?.role || "").toLowerCase();
  const isExpert = role === "expert";
  const isFO = role === "family_office";

  const [clients, setClients] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");

  const [selectedId, setSelectedId] = useState(clientId || "");
  const [pref, setPref] = useState({ regime: "", notes: "" });
  const [prefLoading, setPrefLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");

  const [manualEmail, setManualEmail] = useState("");
  const [addingManual, setAddingManual] = useState(false);

  // ---------- Charger la liste clients ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) return;
      if (!isExpert && !isFO && !clientId) return;

      setListLoading(true);
      setListError("");
      try {
        let arr = [];

        if (clientId) {
          const { data: p, error: e1 } = await supabase
            .from("profiles")
            .select("id, first_name, last_name, email")
            .eq("id", clientId)
            .single();
          if (!e1 && p) {
            arr = [
              {
                id: p.id,
                name:
                  `${p.first_name || ""} ${p.last_name || ""}`.trim() ||
                  p.email ||
                  p.id,
                email: p.email || "",
              },
            ];
          }
        } else {
          const { data: can } = await supabase
            .from("collection_permissions")
            .select("client_id")
            .eq("grantee_id", user.id)
            .eq("can_view", true);

          const ids = [...new Set((can || []).map((r) => r.client_id).filter(Boolean))];
          if (ids.length) {
            const { data: profs } = await supabase
              .from("profiles")
              .select("id, first_name, last_name, email")
              .in("id", ids);

            arr = (profs || []).map((p) => ({
              id: p.id,
              name:
                `${p.first_name || ""} ${p.last_name || ""}`.trim() ||
                p.email ||
                p.id,
              email: p.email || "",
            }));
          }
        }

        const byId = {};
        arr.forEach((c) => (byId[c.id] = c));
        const list = Object.values(byId).sort((a, b) =>
          (a.name || "").localeCompare(b.name || "")
        );

        if (!cancelled) {
          setClients(list);
          if (!clientId && !selectedId && list.length) setSelectedId(list[0].id);
        }
      } catch {
        if (!cancelled) setListError(t("fiscal.error.loadClients"));
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, isExpert, isFO, clientId, selectedId, t]);

  // ---------- Charger la préférence ----------
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!selectedId) {
        setPref({ regime: "", notes: "" });
        return;
      }

      setPrefLoading(true);
      setSaveError("");

      const { data, error } = await supabase
        .from("client_fiscal_prefs")
        .select("regime, notes")
        .eq("client_id", selectedId)
        .maybeSingle();

      if (!cancelled) {
        if (error) setPref({ regime: "", notes: "" });
        else setPref({ regime: data?.regime || "", notes: data?.notes || "" });
      }
      if (!cancelled) setPrefLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const canEditSelected = useMemo(() => {
    if (!user?.id || !selectedId) return false;
    if (clientId) return true;
    return true;
  }, [user?.id, selectedId, clientId]);

  // ---------- Sauvegarde ----------
  const save = async (patch) => {
    if (!selectedId) return;
    setSaveLoading(true);
    setSaveError("");

    try {
      const next = { ...pref, ...patch };
      const { error } = await supabase
        .from("client_fiscal_prefs")
        .upsert(
          {
            client_id: selectedId,
            regime: next.regime || null,
            notes: next.notes || null,
            updated_by: user.id,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "client_id" }
        );

      if (error) throw error;
      setPref(next);
    } catch {
      setSaveError(t("fiscal.error.save"));
    } finally {
      setSaveLoading(false);
    }
  };

  // ---------- Ajout manuel ----------
  const addManualByEmail = async () => {
    const email = manualEmail.trim().toLowerCase();
    if (!email) return;
    setAddingManual(true);
    setListError("");

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email")
        .eq("email", email)
        .single();

      if (error || !data) throw error || new Error("not found");

      const c = {
        id: data.id,
        name:
          `${data.first_name || ""} ${data.last_name || ""}`.trim() ||
          data.email ||
          data.id,
        email: data.email || "",
      };

      setClients((prev) => {
        const exists = prev.some((p) => p.id === c.id);
        const arr = exists ? prev : [...prev, c];
        return arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      });

      setSelectedId(c.id);
      setManualEmail("");
    } catch {
      setListError(t("fiscal.error.emailNotFound"));
    } finally {
      setAddingManual(false);
    }
  };

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === selectedId) || null,
    [clients, selectedId]
  );

  return (
    <div className="p-6 bg-white shadow rounded-lg">
      <h2 className="text-xl font-semibold mb-3">
        {t("fiscal.title", { role: isFO ? t("fiscal.role.fo") : t("fiscal.role.expert") })}
      </h2>

      <p className="text-sm text-gray-600 mb-4">
        {t("fiscal.description")}
      </p>

      {/* Sélection client */}
      {!clientId && (
        <div className="mb-4">
          <label className="text-xs text-gray-500">{t("fiscal.client.label")}</label>

          <select
            className="w-full border border-gray-300 p-2 rounded"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={listLoading}
          >
            <option value="">
              {listLoading ? t("fiscal.client.loading") : t("fiscal.client.select")}
            </option>

            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} {c.email ? `· ${c.email}` : ""}
              </option>
            ))}
          </select>

          {!listLoading && clients.length === 0 && (
            <div className="mt-3 p-3 border border-amber-200 bg-amber-50 rounded">
              <div className="text-xs text-amber-800 mb-2">
                {t("fiscal.client.emptyList")}
              </div>
              <div className="flex gap-2">
                <input
                  type="email"
                  className="flex-1 border border-gray-300 p-2 rounded"
                  placeholder={t("fiscal.client.placeholder")}
                  value={manualEmail}
                  onChange={(e) => setManualEmail(e.target.value)}
                />
                <button
                  onClick={addManualByEmail}
                  disabled={addingManual || !manualEmail}
                  className="px-3 py-2 rounded text-white"
                  style={{ background: addingManual ? "#9ca3af" : "#111" }}
                >
                  {addingManual ? t("fiscal.client.adding") : t("fiscal.client.add")}
                </button>
              </div>
            </div>
          )}

          {listError && <p className="text-xs text-red-600 mt-2">{listError}</p>}
        </div>
      )}

      {/* Bloc préférence */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Régime */}
        <div>
          <label className="text-xs text-gray-500">{t("fiscal.regime.label")}</label>

          <select
            className="w-full border border-gray-300 p-2 rounded"
            value={pref.regime}
            onChange={(e) => save({ regime: e.target.value })}
            disabled={!selectedId || prefLoading || saveLoading || !canEditSelected}
          >
            {REGIMES.map((r) => (
              <option key={r.v} value={r.v}>
                {t(r.labelKey)}
              </option>
            ))}
          </select>

          {prefLoading && <p className="text-xs text-gray-500 mt-2">{t("fiscal.loading.pref")}</p>}
          {saveLoading && <p className="text-xs text-gray-500 mt-2">{t("fiscal.loading.save")}</p>}
          {saveError && <p className="text-xs text-red-600 mt-2">{saveError}</p>}
        </div>

        {/* Notes */}
        <div>
          <label className="text-xs text-gray-500">{t("fiscal.notes.label")}</label>

          <textarea
            className="w-full border border-gray-300 p-2 rounded"
            rows={4}
            placeholder={t("fiscal.notes.placeholder")}
            value={pref.notes}
            onChange={(e) => save({ notes: e.target.value })}
            disabled={!selectedId || prefLoading || saveLoading || !canEditSelected}
          />

          {selectedClient && (
            <p className="text-xs text-gray-500 mt-3">
              {t("fiscal.notes.clientLabel")}{" "}
              <strong>{selectedClient.name}</strong>
              {selectedClient.email ? ` · ${selectedClient.email}` : ""}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 text-xs text-gray-500">
        {t("fiscal.hint")}
      </div>
    </div>
  );
}
